import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../classes/appApi';
import { useOffline } from '../components/App/AppOfflineContext';
import BusinessFunctionBadge from '../components/BusinessFunctionBadge';
import EqxSurvey from '../components/EqxSurvey';
import MainLayout, { useMainLayoutContext } from '../components/MainLayout';
import { ContinuumFormModel } from '../defs/defs';
import EqxButton from '../shared/components/EqxButton/EqxButton';
import EqxCard from '../shared/components/EqxCard/EqxCard';
import EqxError from '../shared/components/EqxError';
import EqxRequestHandler from '../shared/components/EqxRequestHandler/EqxRequestHandler';
import useApiEndpoint from '../shared/hooks/ApiEndpoint';
import useLocation from '../shared/hooks/Location';

type ContinuumFormContentProps = {
    formData: ContinuumFormModel;
    formId: string | number;
    taskID?: string | number;
    onComplete?: () => void;
}

/**
 * Processes the form response to convert boolean field values to strings
 * @param response - The form response object with fields array
 * @returns Processed response with boolean fieldValue properties converted to strings
 */
const processResponse = (response: any): any => {
    if (!response || typeof response !== 'object') {
        return response;
    }

    const processedResponse = { ...response };

    // Process the fields array if it exists
    if (Array.isArray(processedResponse.fields)) {
        processedResponse.fields = processedResponse.fields.map((field: any) => {
            if (field && typeof field === 'object' && 'fieldValue' in field) {
                const processedField = { ...field };

                // Convert boolean fieldValue to string - do this here rather than in shared layer because can't be sure that other endpoints handle in the same way
                processedField.fieldValue = processedField.fieldValue?.toString();

                return processedField;
            }
            return field;
        });
    }

    return processedResponse;
};

const ContinuumFormContent = ({
    formData,
    formId,
    taskID,
    onComplete
}: ContinuumFormContentProps) => {

    const api = useApi()
    const mainLayoutContext = useMainLayoutContext()
    const {
        addSuccessMessage: showGlobalSuccess,
        addErrorMessage: showGlobalError,
        addLoadingMessage: showGlobalLoading,
    } = mainLayoutContext.messages

    const { location: userLocation, status: locationStatus } = useLocation()

    const handleFormComplete = useCallback((response: any, model: any) => {
        showGlobalLoading('Submitting form...', 'submit-form');

        // Process the response to ensure all field values are strings
        const processedResponse = processResponse(response);

        // Add location to the response if available and successful
        const responseWithLocation = userLocation && locationStatus === 'success'
            ? {
                ...processedResponse,
                location: {
                    latitude: userLocation.lat,
                    longitude: userLocation.lng
                }
            }
            : processedResponse;

        // Add taskID to the response if provided
        const finalResponse = taskID
            ? { ...responseWithLocation, taskID }
            : responseWithLocation;

        return api.endpoints.submitContinuumForm(formId.toString())
            .execute(api, finalResponse)
            .then(() => {
                showGlobalSuccess('Form submitted successfully!', 'submit-form');
                if (onComplete) {
                    onComplete();
                }
            })
            .catch(err => {
                console.error('Error submitting form:', err);
                showGlobalError('Error submitting form. Please try again.', 'submit-form');
                throw err; // Rethrow to indicate failure to SurveyComponent
            });
    }, [formId, userLocation, locationStatus, taskID, onComplete]);

    return (
        <EqxCard
            isSection
            collapsible={false}
            className="mb-3 no-padding"
            header={<div className="d-flex">
                {formData.businessFunction && (
                    <div className="m-1 me-2">
                        <BusinessFunctionBadge businessFunction={formData.businessFunction} />
                    </div>
                )}
                <div>
                    <h5 className="mb-1">{formData.title}</h5>
                    <div className="text-muted">{formData.reference} {formData.category && <span className="badge bg-secondary">{formData.category.name}</span>}</div>
                </div>
            </div>}
        >
            <EqxSurvey
                definition={formData.definition}
                onComplete={handleFormComplete}
            />
        </EqxCard>
    );
}

type ContinuumFormProps = {
    formId: string | number;
    taskID?: string | number;
    onComplete?: () => void;
}

const ContinuumForm = ({ formId, taskID, onComplete }: ContinuumFormProps) => {
    const api = useApi()
    const { isOffline } = useOffline()
    const [showForm, setShowForm] = useState(false)

    useEffect(() => {
        if (!isOffline) {
            setShowForm(true)
        }
    }, [isOffline])

    const { response: form, loading, error } = useApiEndpoint<ContinuumFormModel>(
        api,
        api.endpoints.continuumForm(formId.toString())
    )

    return (
        <EqxRequestHandler
            state={{ loading, error: error ?? null }}
            isEmpty={!form}
            empty={<div className="p-5 text-center text-muted">Form not found</div>}
            content={() => (
                !showForm ? (
                    <div className="p-5 text-center text-muted">
                        <p className="mb-3">Forms cannot be submitted while offline. You can still view the form, but won't be able to submit it.</p>
                        <EqxButton
                            variant="primary"
                            onClick={() => setShowForm(true)}
                            icon="fas fa-eye"
                        >
                            View Form Anyway
                        </EqxButton>
                    </div>
                ) : (
                    <ContinuumFormContent
                        formData={form!}
                        formId={formId}
                        taskID={taskID}
                        onComplete={onComplete}
                    />
                )
            )}
        />
    )
}

const ContinuumFormDetailContent = () => {
    const { id } = useParams<{ id: string }>()
    const [searchParams] = useSearchParams()
    const taskIdParam = searchParams.get('taskId')
    const taskIDParsed =
        taskIdParam != null && taskIdParam !== '' ? Number(taskIdParam) : undefined
    const taskID = taskIDParsed !== undefined && !Number.isNaN(taskIDParsed) ? taskIDParsed : undefined
    const mainContext = useMainLayoutContext()

    if (!mainContext.permissions?.canViewContinuumForms) {
        return <EqxError error="You do not have permission to view Continuum Forms." />
    }

    return <ContinuumForm formId={id!} taskID={taskID} />
}

const PageContinuumFormDetail = () => {
    return (
        <MainLayout
            pageTitle="Form Details"
            relatedLinks={[
                { url: '/forms', text: 'Back to Forms' }
            ]}
        >
            <ContinuumFormDetailContent />
        </MainLayout>
    )
}

export default PageContinuumFormDetail
export { ContinuumForm };

